import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { useNavigate } from 'react-router-dom';
import DashboardMetricCard from '../Cards/DashboardMetricCard';
import GlassCard from '../Cards/GlassCard';

/**
 * @param {object} props
 * @param {number | null} [props.assignmentCount]
 * @param {boolean} [props.assignmentLoading]
 * @param {{ totalSolved?: number, thisWeekSolved?: number, currentStreak?: number } | null} [props.codingSummary]
 * @param {boolean} [props.codingSummaryLoading]
 */
export default function StudentDashboardMetrics({
  assignmentCount,
  assignmentLoading = false,
  codingSummary = null,
  codingSummaryLoading = false,
}) {
  const navigate = useNavigate();
  const totalSolved = Number(codingSummary?.totalSolved || 0);
  const thisWeekSolved = Number(codingSummary?.thisWeekSolved || 0);
  const currentStreak = Number(codingSummary?.currentStreak || 0);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
        gap: 2.5,
      }}
    >
      <Box>
        <DashboardMetricCard
          to="/student/courses"
          icon={<MenuBookOutlinedIcon fontSize="small" />}
          label="Courses"
          value="Open"
          loading={false}
        />
      </Box>
      <Box>
        <DashboardMetricCard
          to="/student/assignments"
          icon={<AssignmentOutlinedIcon fontSize="small" />}
          label="Assignments"
          value={assignmentCount ?? '—'}
          loading={assignmentLoading}
        />
      </Box>
      <Box>
        <GlassCard
          hoverable
          onClick={() => navigate('/student/progress')}
          sx={{
            height: '100%',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: (theme) => theme.shadows[6],
            },
          }}
        >
          <Box
            sx={{
              p: 2.5,
              height: '100%',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', minHeight: 108 }}>
              <Box
                sx={{
                  p: 1.125,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                  color: 'primary.main',
                  display: 'flex',
                  flexShrink: 0,
                }}
              >
                <TrendingUpOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', letterSpacing: '0.08em', fontWeight: 600, lineHeight: 1.4, mb: 0.75 }}
                >
                  Coding progress
                </Typography>

                {codingSummaryLoading ? (
                  <Skeleton width={120} />
                ) : (
                  <>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                      {totalSolved === 0 ? 'Start Coding' : `${totalSolved} Solved`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                      {thisWeekSolved === 0 ? 'No progress this week' : `+${thisWeekSolved} this week`}
                    </Typography>
                    {currentStreak > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
                        {`🔥 ${currentStreak} day streak`}
                      </Typography>
                    ) : null}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </GlassCard>
      </Box>
    </Box>
  );
}
